import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/* ROTAS69: сколько держим готовые ответы секции и сколько их помним.
   Минута — компромисс: щёлканье вариантами укладывается в неё целиком, а устаревшее наличие
   дольше минуты не живёт. */
const SECTION_CACHE_TTL = 60000;
const SECTION_CACHE_MAX = 8;

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * PERF: write one of the two checked states to the radio AND to its label.
 *
 * The sliding-pill rules used to reach into the input from the label with
 * `:has([data-current-checked='true'])`, and every :has() on a page is re-matched on every DOM
 * insertion anywhere in the document — the tax that makes the first seconds after load stutter
 * while app scripts insert nodes. With the state on the label too, those selectors become plain
 * attribute matches. Both copies are written here in the same tick, so there is no moment where
 * they disagree, and morph.js preserves these attribute names on any element, so the label keeps
 * its copy through a re-render. The input keeps its own copy: nothing that read it had to change.
 *
 * @param {HTMLInputElement} radio - The radio input.
 * @param {'currentChecked' | 'previousChecked'} key - Which state to write.
 * @param {'true' | 'false'} value - The value to write.
 */
function setCheckedState(radio, key, value) {
  radio.dataset[key] = value;
  const label = radio.closest('label');
  if (label) label.dataset[key] = value;
}

/**
 * PERF: mirror the pill's sibling geometry onto attributes, same story as setCheckedState above.
 *
 * The sliding-pill rules used to derive "is this label before or after the checked one" and
 * "is the checked label the last one" with sibling `:has()` selectors — the remaining family of
 * document-insert re-match taxes on the PDP. The server renders the same attributes
 * (variant-main-picker.liquid), this keeps them in step on every change, and morph.js preserves
 * the names through a re-render.
 *
 * @param {HTMLFieldSetElement} fieldset - The option group.
 * @param {HTMLInputElement[]} radios - The group's radios in DOM order.
 * @param {number} currentIndex - Index of the newly checked radio.
 * @param {number | undefined} previousIndex - Index of the previously checked radio, if any.
 */
function syncPillGeometry(fieldset, radios, currentIndex, previousIndex) {
  radios.forEach((radio, index) => {
    const label = radio.closest('label');
    if (!label) return;
    label.dataset.checkedPos = index === currentIndex ? 'current' : index < currentIndex ? 'before' : 'after';
    if (index === currentIndex && previousIndex !== undefined && previousIndex !== currentIndex) {
      label.dataset.moved = previousIndex > currentIndex ? 'back' : 'fwd';
    } else {
      delete label.dataset.moved;
    }
  });
  fieldset.dataset.checkedEdge =
    currentIndex === 0 ? 'first' : currentIndex === radios.length - 1 ? 'last' : 'mid';
}

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  /* ROTAS69: метка текущей подмены фотографии: отложенные повторы старой подмены должны замолчать,
     как только пришёл ответ сервера. */
  #mediaSwapToken = 0;

  /* ROTAS69: запросы секции, которые сейчас в пути: адрес -> обещание с текстом ответа.
     Держим именно обещание, а не адрес: прогрев по нажатию стартует за 60–150 мс до клика и к
     моменту клика ещё летит, так что клик должен ПОДХВАТИТЬ его, а не слать второй такой же
     запрос (замер 22.09.2026: с набором адресов уходило два ответа по 189 КБ вместо одного). */
  #inflight = new Map();

  /* ROTAS69: ответы секции, уже полученные на этой странице, живут здесь: ключ — адрес запроса.
     Повторный выбор того же значения (покупатель щёлкает буквы туда-сюда) отдаётся из памяти
     мгновенно, без обращения к серверу. Срок жизни короткий: наличие товара может измениться,
     а добавление в корзину всё равно проверяет сервер. */
  #sectionCache = new Map();

  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    // ROTAS69: греем ответ уже на нажатии, см. #warmOptionOnPress.
    this.addEventListener('pointerdown', this.#warmOptionOnPress, { passive: true });

    // PERF mirror for `:has(:focus-visible)`: the focus ring rules key on
    // [data-focus-visible='true'] on the label instead of reaching into the input with :has()
    // (same document-insert re-match tax as the checked mirrors above).
    this.addEventListener('focusin', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      const label = input.closest('label.variant-option__button-label');
      if (label) label.dataset.focusVisible = input.matches(':focus-visible') ? 'true' : 'false';
    });
    this.addEventListener('focusout', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      const label = input.closest('label.variant-option__button-label');
      if (label) delete label.dataset.focusVisible;
    });
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    // A click commits a new baseline: drop every hover-preview cache so a later
    // pointerleave restores the newly selected price/image, not the pre-click one.
    this.originalPriceHTML = null;
    this.originalSpecsHTML = null;
    this.originalMaterialHTML = null;
    this.fetchingPreviewVariantId = null;
    this.originalImageSrc = null;
    this.originalImageSrcset = null;
    this.originalPictureSources = null;
    this.originalImageElement = null;
    this.originalSwatchValueElement = null;
    this.originalSwatchValueHTML = null;

    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // ROTAS69: the material line under the title follows the chosen value at once, from the
    // data-materials the server printed on it (product cards do the same in product-card.js).
    this.#setMaterialLine(selectedOption);
    this.#setPriceNow();
    this.#showOptionMediaNow(selectedOption);
    this.#showVariantPhotosNow(selectedOption);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const optionValueId = selectedOption.dataset.optionValueId ?? '';
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, optionValueId);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            setCheckedState(radios[currentIndex], 'previousChecked', 'false');
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            setCheckedState(radios[previousIndex], 'previousChecked', 'false');
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            setCheckedState(radios[newCurrentIndex], 'currentChecked', 'true');
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            setCheckedState(radios[newPreviousIndex], 'previousChecked', 'true');
            setCheckedState(radios[newPreviousIndex], 'currentChecked', 'false');
          }

          if (newCurrentIndex !== undefined) {
            syncPillGeometry(fieldset, radios, newCurrentIndex, newPreviousIndex);
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * ROTAS69: адрес запроса одной секции. Один сборщик на два места — на смену варианта и на
   * прогрев по нажатию, иначе ключи в памяти разойдутся и прогрев окажется впустую.
   * @param {string} base - Адрес товара.
   * @param {string} sectionId - Идентификатор своей секции.
   * @param {string[]} params - Остальные параметры запроса.
   * @returns {string}
   */
  #sectionRequestUrl(base, sectionId, params) {
    const clean = base?.includes('?') ? base.split('?')[0] : base;
    return `${clean}?section_id=${sectionId}&${params.join('&')}`;
  }

  /**
   * ROTAS69: кладёт готовый ответ в память страницы.
   * @param {string} url - Ключ.
   * @param {string} text - Ответ.
   */
  #rememberSection(url, text) {
    // Перезапись без удаления оставила бы ключ на старом месте очереди, и вымывался бы как раз
    // тот вариант, к которому человек только что возвращался.
    this.#sectionCache.delete(url);
    // Больше SECTION_CACHE_MAX не держим: у товара с 26 буквами это 26 ответов, а нужны
    // последние, между которыми человек и ходит.
    if (this.#sectionCache.size >= SECTION_CACHE_MAX) {
      this.#sectionCache.delete(this.#sectionCache.keys().next().value);
    }
    this.#sectionCache.set(url, { at: Date.now(), text });
  }

  /**
   * ROTAS69: запрашивает секцию, переиспользуя уже летящий запрос по тому же адресу.
   * @param {string} url - Адрес запроса.
   * @param {AbortSignal} [signal] - Сигнал отмены обычной смены варианта; у прогрева его нет.
   * @returns {Promise<string>}
   */
  #fetchSection(url, signal) {
    const alreadyFlying = this.#inflight.get(url);
    if (alreadyFlying) return alreadyFlying;

    const flight = fetch(url, signal ? { signal } : undefined)
      .then((response) => {
        // Ответ 404 или 500 — это страница ошибки. Отдать её дальше значит перерисовать блок
        // товара мусором, а положить в память — повторять мусор целую минуту.
        if (!response.ok) throw new Error(`section request failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        // В память кладём только ответы одной секции: полный документ комбинированного товара
        // весит полтора мегабайта, восемь таких съели бы память вкладки.
        if (url.includes('section_id=')) this.#rememberSection(url, text);
        return text;
      })
      .finally(() => {
        this.#inflight.delete(url);
      });

    this.#inflight.set(url, flight);
    return flight;
  }

  /**
   * ROTAS69: радиокнопка набора по её значению.
   * @param {Element | null} fieldset - Набор значений одной опции.
   * @param {string | null} value - Значение.
   * @returns {HTMLInputElement | null}
   */
  #radioByValue(fieldset, value) {
    if (!fieldset || value == null) return null;
    const found = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
      (radio) => /** @type {HTMLInputElement} */ (radio).value === value
    );
    return /** @type {HTMLInputElement | null} */ (found ?? null);
  }

  /**
   * ROTAS69: значения опций так, как они будут выглядеть ПОСЛЕ выбора этой кнопки.
   * @param {HTMLInputElement} input - Кнопка, которую вот-вот нажмут.
   * @returns {string[]}
   */
  #optionValuesWith(input) {
    const fieldset = input.closest('fieldset');
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map((el) =>
      el.closest('fieldset') === fieldset ? input.dataset.optionValueId ?? '' : (/** @type {HTMLElement} */ (el)).dataset.optionValueId ?? ''
    );
  }

  /**
   * ROTAS69: начинает запрос в момент НАЖАТИЯ, не дожидаясь отпускания пальца.
   *
   * Между pointerdown и click у живого человека 60–150 мс, и всё это время канал простаивал.
   * Ответ кладётся в ту же память, откуда его через мгновение возьмёт обычная смена варианта —
   * адрес собирается тем же #sectionRequestUrl, иначе ключ не совпадёт. Комбинированные товары
   * не греем: у них своя ссылка и весь документ, греть полтора мегабайта на касание нельзя.
   * @param {Event} event - Событие нажатия.
   */
  #warmOptionOnPress = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (this.dataset.templateProductMatch !== 'true') return;
    if (this.closest('product-card') || this.closest('quick-add-dialog')) return;

    /* Пункт нашего выпадающего списка — это <button data-r69dd-opt>, а не ярлык с радиокнопкой:
       нужную радиокнопку r69-option-select.js ставит уже после клика. Ищем её по значению, иначе
       прогрев не работал бы ровно там, где он нужнее всего — у списка из 26 букв. */
    const ddOption = target.closest('[data-r69dd-opt]');
    if (ddOption?.classList.contains('r69dd-soldout')) return;

    // Панель списка висит РЯДОМ с набором значений, а не внутри него: r69-option-select.js кладёт
    // её в форму пикера и связывает с набором атрибутами data-r69dd-for -> data-r69dd-host.
    // Идём тем же путём, иначе радиокнопку не найти (проверено на живой странице: closest('fieldset')
    // от пункта списка даёт null).
    const ddHost = ddOption
      ? this.querySelector(`[data-r69dd-host="${ddOption.closest('[data-r69dd]')?.getAttribute('data-r69dd-for')}"]`)
      : null;

    const input = ddOption
      ? this.#radioByValue(ddHost, ddOption.getAttribute('data-r69dd-value'))
      : target.closest('label')?.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement) || input.checked || input.disabled) return;
    if (input.dataset.connectedProductUrl) return;

    const sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
    const base = this.dataset.productUrl;
    if (!sectionId || !base) return;

    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    params.push(`option_values=${this.#optionValuesWith(input).join(',')}`);

    const url = this.#sectionRequestUrl(base, sectionId, params);
    const remembered = this.#sectionCache.get(url);
    if (remembered && Date.now() - remembered.at < SECTION_CACHE_TTL) return;

    this.#fetchSection(url).catch(() => {
      /* не доехало — обычная смена варианта сходит за ответом сама */
    });
  };

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    /* ROTAS69: на странице товара просим у Shopify ТОЛЬКО товарную секцию, а не весь документ.
       Замер 22.09.2026, /products/kulons-31 (26 букв, у каждой своя фотография): ответ всего
       документа 1202 КБ (150 КБ по проводу), данные в DOM обновлялись через 862 мс на десктопе
       и 929 мс на телефоне с процессором вчетверо медленнее; ответ одной секции — 184 КБ
       (32 КБ по проводу), те же 425 и 690 мс. Остальное время у покупателя съедает мобильный
       канал: полтора мегабайта разметки он тянет и разбирает секундами.
       Брать секцию безопасно: всё, что тема читает из ответа, лежит внутри неё — пикер и его
       JSON варианта, цена, галерея, наличие, форма, липкая кнопка, таблица характеристик.
       Сверено тег в тег с тем же куском полного документа, расхождений нет. Предпросмотр по
       наведению в этом же файле секцию просит давно.
       Комбинированный товар (другая ссылка у значения) по-прежнему тянет весь документ: там
       меняется и содержимое соседних секций. */
    const ownSectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
    const currentProductUrl = this.dataset.productUrl?.split('?')[0];
    const connectedUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = !!connectedUrl && connectedUrl !== currentProductUrl;

    if (
      !source &&
      ownSectionId &&
      !loadsNewProduct &&
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog')
    ) {
      return this.#sectionRequestUrl(productUrl, ownSectionId, params);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {string} [optionValueId] - The selected option value ID for event detail.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, optionValueId = '') {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
        },
        promise: deferredEventPromise.promise,
      })
    );

    const { signal } = this.#abortController;
    const remembered = this.#sectionCache.get(requestUrl);
    const responseSource =
      remembered && Date.now() - remembered.at < SECTION_CACHE_TTL
        ? Promise.resolve(remembered.text)
        : this.#fetchSection(requestUrl, signal);

    responseSource
      .then((responseText) => {
        // Ответ мог прийти по запросу, начатому прогревом, — такой запрос не отменяется
        // сигналом. Поэтому проверяем сами: если человек успел выбрать другое значение,
        // этот ответ уже не про то, что на экране.
        if (signal.aborted) return;
        // Ответ пришёл — отложенные повторы подмены фотографии больше не нужны и не должны
        // трогать свежую галерею.
        this.#mediaSwapToken += 1;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * ROTAS69: ставит ВСЕ снимки выбранного варианта сразу, не дожидаясь ответа сервера.
   *
   * Галерея собирается на сервере, снимков чужих вариантов в разметке нет — поэтому раньше
   * мгновенно менялся только первый снимок, а остальные ждали ответ (замер 22.09.2026: первый
   * 146–260 мс, остальные 499–503 мс). Сниппет галереи печатает рядом справочник
   * data-r69-variant-media: у каждого снимка его метки из альт-текста (включая двойные через
   * «+»), у каждого значения опции — его ключи. Здесь по нему подбираются снимки нового набора
   * значений и подменяются адреса у уже нарисованных картинок в том же порядке, что отдаст
   * сервер: первым главный снимок варианта, затем остальные по порядку товара; снимки с метками
   * других вариантов и главные снимки других вариантов не берутся. Ответ сервера потом перерисует
   * галерею начисто и добавит то, чего в разметке не было.
   */
  #showVariantPhotosNow(optionElement) {
    if (this.closest('product-card') || this.closest('quick-add-dialog')) return;

    const section = this.closest('.shopify-section') || document;
    const holder = section.querySelector('script[type="application/json"][data-r69-variant-media]');
    if (!holder) return;

    let map;
    try {
      map = JSON.parse(holder.textContent || '{}');
    } catch (error) {
      return;
    }
    if (!map || !Array.isArray(map.media) || !map.values) return;

    const chosen = [];
    this.querySelectorAll('fieldset input:checked').forEach((input) => chosen.push(input.value));
    this.querySelectorAll('select').forEach((select) => chosen.push(select.value));

    const keys = new Set();
    for (const value of chosen) (map.values[value] || []).forEach((key) => keys.add(key));
    if (!keys.size) return;

    const mediaId = optionElement?.dataset?.optionMediaId;
    const lead = mediaId ? map.media.find((m) => String(m.id) === String(mediaId)) : null;
    const all = lead ? [lead].concat(map.media.filter((m) => m !== lead && !m.v)) : map.media;
    const fitting = all.filter((m) => !m.tags || !m.tags.length || m.tags.every((tag) => keys.has(tag)));
    const order = fitting.length ? fitting : all;
    const token = (this.#mediaSwapToken += 1);

    // Слайдшоу и сетка — две отдельные копии одних и тех же снимков, каждой свой список.
    for (const selector of ['media-gallery slideshow-slide img', 'media-gallery .media-gallery__grid img']) {
      const imgs = Array.from(section.querySelectorAll(selector));
      imgs.forEach((img, index) => {
        const want = order[index];
        if (!want || !(img instanceof HTMLImageElement)) return;

        // Ширину берём у самой картинки: сетка на компьютере и слайд на телефоне просят разное,
        // а справочник хранит один адрес.
        const width = (img.getAttribute('src') || '').match(/[?&]width=(\d+)/)?.[1];
        const url = width ? want.src.replace(/([?&]width=)\d+/, `$1${width}`) : want.src;

        const apply = () => {
          if (token !== this.#mediaSwapToken) return;
          img.removeAttribute('srcset');
          img.setAttribute('src', url);
          img.closest('picture')?.querySelectorAll('source').forEach((source) => source.removeAttribute('srcset'));
        };

        apply();
        requestAnimationFrame(() => {
          apply();
          setTimeout(apply, 10);
          setTimeout(apply, 50);
        });
      });
    }
  }

  /**
   * ROTAS69: показывает фотографию выбранного значения СРАЗУ, не дожидаясь ответа сервера.
   *
   * Ответ секции приходит за 0,4–0,6 с, и всё это время покупатель видит фотографию прошлого
   * варианта — на телефоне это и читается как «данные меняются долго». Идентификатор медиа уже
   * напечатан на самой кнопке (data-option-media-id), а слайд с этой фотографией уже лежит в
   * галерее, поэтому переключение стоит один вызов и сети не требует. Тем же приёмом работает
   * предпросмотр по наведению мышью на компьютере. Ответ сервера потом перерисует галерею
   * штатно и подтвердит тот же слайд.
   * @param {HTMLElement} optionElement - Выбранное значение: input у кнопок, option у списка.
   */
  #showOptionMediaNow(optionElement) {
    if (this.closest('product-card') || this.closest('quick-add-dialog')) return;

    const mediaId = optionElement?.dataset?.optionMediaId;
    const mediaUrl =
      optionElement?.dataset?.mediaUrl || optionElement?.closest('label')?.getAttribute('data-media-url');
    if (!mediaId && !mediaUrl) return;

    const section = this.closest('.shopify-section') || document;
    const mediaGallery = /** @type {any} */ (section.querySelector('media-gallery'));
    if (!mediaGallery) return;

    const slideshow = mediaGallery.slideshow;
    const slides = slideshow?.refs?.slides || [];
    const targetSlide = mediaId
      ? slides.find((/** @type {any} */ slide) => slide.getAttribute('slide-id') == mediaId)
      : null;

    if (targetSlide) {
      targetSlide.hidden = false;
      targetSlide.setAttribute('reveal', '');
      targetSlide.setAttribute('aria-hidden', 'false');

      for (const slide of slides) {
        if (slide === targetSlide) continue;
        slide.setAttribute('aria-hidden', 'true');
        slide.removeAttribute('reveal');
        if (slide.getAttribute('variant-image') != null) slide.hidden = true;
      }

      if (typeof slideshow.select === 'function') {
        const done = slideshow.select({ id: mediaId.toString() }, undefined, { animate: false });
        if (done && typeof done.catch === 'function') done.catch(() => {});
      }
    }

    /* На десктопе в раскладке «сетка» слайдшоу спрятано (display: none), и переключение слайда
       покупатель не увидит: видимая картинка живёт в .media-gallery__grid. Поэтому там, а также
       когда подходящего слайда нет совсем (тема прячет фотографии чужих вариантов — у кулона с
       буквами в галерее ровно один слайд), подменяем адрес у ВИДИМОЙ картинки. Точно так же
       поступает предпросмотр по наведению. */
    const isGridDesktop =
      mediaGallery.dataset.presentation === 'grid' && window.matchMedia('(min-width: 750px)').matches;
    if (targetSlide && !isGridDesktop) return;
    if (!mediaUrl) return;

    const img = /** @type {HTMLImageElement | null} */ (
      isGridDesktop
        ? section.querySelector('.media-gallery__grid .product-media img')
        : section.querySelector('media-gallery slideshow-slide[aria-hidden="false"] img') ||
          section.querySelector('media-gallery .product-media__image') ||
          section.querySelector('media-gallery img')
    );
    if (!img) return;

    const url = mediaUrl.startsWith('//') ? `https:${mediaUrl}` : mediaUrl;
    const token = (this.#mediaSwapToken += 1);
    const apply = () => {
      // Метка: ответ сервера уже пришёл и перерисовал галерею — наши отложенные повторы обязаны
      // замолчать, иначе они сорвут srcset со свежей картинки.
      if (token !== this.#mediaSwapToken) return;
      img.removeAttribute('srcset');
      // sizes НЕ трогаем: morph.js переносит на новый узел живое значение этого атрибута, и
      // снятый sizes остался бы снятым навсегда — браузер выбирал бы кандидата вдвое шире нужного.
      img.setAttribute('src', url);
      img.closest('picture')?.querySelectorAll('source').forEach((source) => source.removeAttribute('srcset'));
    };

    // Ленивый загрузчик темы переписывает src/srcset асинхронно и может отменить одну подмену —
    // повторяем несколько кадров, ровно как в предпросмотре по наведению.
    apply();
    requestAnimationFrame(() => {
      apply();
      setTimeout(apply, 10);
      setTimeout(apply, 50);
    });
  }

  /**
   * ROTAS69: цена ставится сразу из таблицы цен вариантов, сервер потом перерисовывает то же самое.
   */
  #setPriceNow() {
    const data = this.querySelector('script[data-r69-variant-prices]');
    if (!data) return;
    /** @type {{ names: string[], variants: { options: string[], price: string, compare: string | null }[] }} */
    let table;
    try {
      table = JSON.parse(data.textContent ?? '');
    } catch {
      return;
    }

    /** @type {Map<string, string | null>} */
    const chosen = new Map();
    for (const input of this.querySelectorAll('input[type="radio"][data-option-name]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const name = input.dataset.optionName ?? '';
      if (!chosen.has(name)) chosen.set(name, null);
      if (input.checked) chosen.set(name, input.value);
    }
    for (const select of this.querySelectorAll('select.variant-option__select')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const option = select.selectedOptions[0];
      if (option?.dataset.optionName) chosen.set(option.dataset.optionName, option.value);
    }

    const values = table.names.map((name) => chosen.get(name));
    const variant = table.variants.find((v) => v.options.every((value, i) => value === values[i]));
    if (!variant) return;

    const decode = (/** @type {string} */ html) => {
      const area = document.createElement('textarea');
      area.innerHTML = html;
      return area.value;
    };
    const price = decode(variant.price);
    const compare = variant.compare ? decode(variant.compare) : '';

    const scope = this.closest('#quick-add-modal-content') ?? this.closest('.shopify-section');
    for (const container of scope?.querySelectorAll('product-price [ref="priceContainer"]') ?? []) {
      const regular = container.querySelector('.price__regular');
      const sale = container.querySelector('.price__sale');
      if (!regular || !sale) continue;
      for (const el of [regular.querySelector('.price'), sale.querySelector('.price-item--sale')]) {
        if (el && el.textContent !== price) el.textContent = price;
      }
      const compareEl = sale.querySelector('.compare-at-price');
      if (compareEl && compareEl.textContent !== compare) compareEl.textContent = compare;
      regular.classList.toggle('price__hidden', Boolean(compare));
      sale.classList.toggle('price__hidden', !compare);
    }
  }

  /**
   * Writes the material line under the product title from a value's `data-materials`; an empty
   * attribute means the product-level line kept in the line's own data-default. Product page
   * only: cards and quick-add keep their line in product-card.js. The text node is mutated in
   * place and equal writes are skipped, the same care product-card.js takes.
   * @param {HTMLElement | null | undefined} value - The chosen or hovered input / option.
   */
  #setMaterialLine(value) {
    if (!value || this.parentProductCard || this.closest('quick-add-dialog')) return;
    const line = this.closest('.shopify-section')?.querySelector('.product-details [data-material-line]');
    if (!line) return;
    const text = value.dataset.materials || line.dataset.default || '';
    if (!text || line.textContent === text) return;
    const [first] = line.childNodes;
    if (line.childNodes.length === 1 && first instanceof Text) {
      first.data = text;
    } else {
      line.textContent = text;
    }
  }

  /**
   * Temporarily previews a variant's media and price while its swatch is hovered.
   * Hover previews are desktop-only: on touch devices `pointerenter` fires on tap,
   * which would preview and then immediately revert the variant the user just picked.
   * @param {any} id - The media id of the variant to preview.
   * @param {any} [event] - The pointer event that triggered the preview.
   */
  previewVariant(id, event) {
    if (event && 'pointerType' in event && event.pointerType === 'touch') return;
    if (!window.matchMedia('(hover: hover)').matches) return;
    if (this.parentProductCard) {
      const card = /** @type {any} */ (this.parentProductCard);
      if (card.previewVariant) {
        card.previewVariant(id, event);
      }
    } else {
      this.previewProductPageVariant(id, event);
    }
  }

  /**
   * Reverts a hover preview back to the actually selected variant.
   * @param {any} [event] - The pointer event that triggered the reset.
   */
  resetVariant(event) {
    if (event && 'pointerType' in event && event.pointerType === 'touch') return;
    if (!window.matchMedia('(hover: hover)').matches) return;
    if (this.parentProductCard) {
      const card = /** @type {any} */ (this.parentProductCard);
      if (card.resetVariantInstant) {
        card.resetVariantInstant();
      }
    } else {
      this.resetProductPageVariant();
    }
  }

  /**
   * Product-page hover preview: swaps the gallery to the hovered variant's media,
   * updates the selected-value label next to the option name, and morphs in the
   * hovered variant's price and spec sheet (fetched via section rendering, cached per variant);
   * the material line under the title comes straight from the swatch's data-materials.
   * @param {any} id - The media id of the variant to preview.
   * @param {any} [event] - The pointer event that triggered the preview.
   */
  previewProductPageVariant(id, event) {
    const section = this.closest('.shopify-section') || document;
    const mediaGallery = /** @type {any} */ (section.querySelector('media-gallery'));
    const priceContainer = section.querySelector('product-price [ref="priceContainer"]');

    if (priceContainer && !this.originalPriceHTML) {
      this.originalPriceHTML = priceContainer.outerHTML;
    }

    if (mediaGallery && id) {
      const slideshow = mediaGallery.slideshow;
      let targetSlide = null;

      if (slideshow) {
        const slides = slideshow.refs.slides || [];
        targetSlide = slides.find((/** @type {any} */ slide) => slide.getAttribute('slide-id') == id);
      }

      if (targetSlide) {
        targetSlide.hidden = false;
        targetSlide.setAttribute('reveal', '');
        targetSlide.setAttribute('aria-hidden', 'false');

        const slides = slideshow.refs.slides || [];
        for (const slide of slides) {
          if (slide !== targetSlide) {
            slide.setAttribute('aria-hidden', 'true');
            slide.removeAttribute('reveal');
            if (slide.getAttribute('variant-image') != null) {
              slide.hidden = true;
            }
          }
        }

        if (slideshow.select) {
          slideshow.select({ id: id.toString() }, undefined, { animate: false }).catch(() => {});
        }
      }

      const isGridDesktop = mediaGallery.dataset.presentation === 'grid' && window.matchMedia('(min-width: 750px)').matches;

      if (!targetSlide || isGridDesktop) {
        // No matching slide (hide_variants pruned it) or the desktop Grid layout,
        // where the slideshow is display:none — preview by swapping the visible
        // image's src directly and remember the original for the reset.
        if (event && event.target instanceof Element) {
          const label = event.target.closest('label');
          const mediaUrl = label ? label.getAttribute('data-media-url') : null;
          if (mediaUrl) {
            let activeSlide = null;
            if (isGridDesktop) {
              activeSlide = section.querySelector('.media-gallery__grid .product-media img');
            } else if (slideshow) {
              const slides = slideshow.refs.slides || [];
              activeSlide = slides.find((/** @type {any} */ slide) => slide.getAttribute('aria-hidden') === 'false') || slides[0];
            }
            if (!activeSlide && !isGridDesktop) {
              activeSlide = section.querySelector('media-gallery slideshow-slide[aria-hidden="false"]') || section.querySelector('media-gallery slideshow-slide') || section.querySelector('media-gallery .product-media__image');
            }

            const img = activeSlide ? (activeSlide.tagName === 'IMG' ? activeSlide : activeSlide.querySelector('img')) : section.querySelector('media-gallery .product-media__image');
            if (img) {
              if (!this.originalImageSrc) {
                this.originalImageSrc = img.src;
                this.originalImageSrcset = img.srcset;
                this.originalImageElement = img;
              }
              img.srcset = '';
              const newUrl = mediaUrl.startsWith('//') ? 'https:' + mediaUrl : mediaUrl;

              /* ROTAS69: метка этого предпросмотра. Повторы ниже идут по таймерам, и без метки
                 они переживали увод мыши: при быстрых наведениях (25 мс на свотч) сброс успевал
                 вернуть фотографию, а следом просыпался таймер прошлого наведения и ставил чужую
                 обратно — на экране оставался не тот вариант, что выбран. Владелец поймал это на
                 витрине 22.09.2026, воспроизведено Playwright. */
              const swapToken = (this.#mediaSwapToken += 1);
              const updateImage = () => {
                if (swapToken !== this.#mediaSwapToken) return;
                if (img.srcset) img.removeAttribute('srcset');
                // `sizes` stays on the node: it is inert while srcset is gone, and the click that
                // follows a hover now morphs the gallery in place (media-gallery.js). Morph keeps
                // the LIVE node's sizes, so stripping it here would hand the restored srcset no
                // sizes and make the browser pick a needlessly large candidate.
                img.setAttribute('src', newUrl);

                const picture = img.closest('picture');
                if (picture) {
                  if (!this.originalPictureSources) {
                    this.originalPictureSources = [];
                    picture.querySelectorAll('source').forEach((source) => {
                      this.originalPictureSources.push({
                        el: source,
                        srcset: source.srcset,
                      });
                    });
                  }
                  picture.querySelectorAll('source').forEach((source) => {
                    source.removeAttribute('srcset');
                  });
                }
              };

              // The theme's lazy loader rewrites src/srcset asynchronously and can
              // undo a single swap — re-apply across a few frames to win the race.
              updateImage();
              requestAnimationFrame(() => {
                updateImage();
                setTimeout(updateImage, 10);
                setTimeout(updateImage, 50);
              });
            }
          }
        }
      }
    }

    if (event && event.target instanceof Element) {
      const label = event.target.closest('label');
      const input = label ? label.querySelector('input[type="radio"]') : null;
      if (input instanceof HTMLInputElement) {
        const fieldset = label.closest('fieldset');
        if (fieldset) {
          const swatchValueElement = fieldset.querySelector('.variant-option__swatch-value');
          if (swatchValueElement) {
            if (!this.originalSwatchValueHTML) {
              this.originalSwatchValueHTML = swatchValueElement.innerHTML;
              this.originalSwatchValueElement = swatchValueElement;
            }
            // For an option linked to a metafield the display text is the metaobject entry's
            // label, not the raw option value — the server put it on the input, because
            // input.value has to stay the value Shopify matches variants on.
            swatchValueElement.textContent = input.dataset.displayLabel || input.value;
          }
        }

        // Material line under the title: straight from the swatch's data-materials, like the
        // cards, so it never waits for the section request below. Snapshot once for the leave.
        const materialLine = section.querySelector('.product-details [data-material-line]');
        if (materialLine && !this.originalMaterialHTML) {
          this.originalMaterialHTML = materialLine.outerHTML;
        }
        this.#setMaterialLine(input);

        const variantId = input.dataset.variantId;
        if (variantId) {
          this.previewPricesCache = this.previewPricesCache || {};
          this.fetchingPreviewVariantId = variantId;

          const specList = section.querySelector('r69-product-specs [data-r69spec-list]');
          if (specList && !this.originalSpecsHTML) {
            this.originalSpecsHTML = specList.outerHTML;
          }

          if (this.previewPricesCache[variantId]) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(this.previewPricesCache[variantId], 'text/html');
            const cachedPriceElement = doc.querySelector(`[ref='priceContainer']`) || doc.body.firstElementChild;
            if (cachedPriceElement && priceContainer) {
              morph(priceContainer, cachedPriceElement);
            }
            const cachedSpecs = doc.querySelector('r69-product-specs [data-r69spec-list]');
            if (cachedSpecs && specList) {
              morph(specList, cachedSpecs);
            }
          } else {
            const productUrl = this.dataset.productUrl || window.location.href;
            if (productUrl) {
              const url = new URL(productUrl, window.location.origin);
              url.searchParams.set('variant', variantId);

              const sectionIdMatch = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
              if (sectionIdMatch) {
                url.searchParams.set('section_id', sectionIdMatch);
              }

              fetch(url.href)
                .then((response) => response.text())
                .then((htmlText) => {
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(htmlText, 'text/html');
                  const newPriceElement = doc.querySelector(`[ref='priceContainer']`) || doc.body.firstElementChild;
                  const newSpecs = doc.querySelector('r69-product-specs [data-r69spec-list]');

                  // Cache the whole response, not just the price: the same fetch also carries the
                  // specification sheet for this variant, and a second request per swatch would be
                  // pure waste.
                  this.previewPricesCache[variantId] = htmlText;

                  // Only apply if this hover is still the latest one — a slow response for a
                  // swatch the pointer already left must not overwrite what is hovered now.
                  if (this.fetchingPreviewVariantId === variantId) {
                    if (newPriceElement) {
                      const currentPriceContainer = section.querySelector('product-price [ref="priceContainer"]');
                      if (currentPriceContainer) {
                        morph(currentPriceContainer, newPriceElement);
                      }
                    }
                    if (newSpecs) {
                      const currentSpecs = section.querySelector('r69-product-specs [data-r69spec-list]');
                      if (currentSpecs) {
                        morph(currentSpecs, newSpecs);
                      }
                    }
                  }
                })
                .catch((err) => console.error('Error fetching preview price:', err));
            }
          }
        }
      }
    }
  }

  /**
   * Restores the product page to the selected variant after a hover preview:
   * price, spec sheet, material line, swapped image src, swatch value label, and the gallery slide.
   */
  resetProductPageVariant() {
    // ROTAS69: увод мыши гасит отложенные повторы подмены фотографии — см. swapToken выше.
    this.#mediaSwapToken += 1;

    const section = this.closest('.shopify-section') || document;
    const mediaGallery = /** @type {any} */ (section.querySelector('media-gallery'));
    const priceContainer = section.querySelector('product-price [ref="priceContainer"]');

    this.fetchingPreviewVariantId = null;

    /*
     * ФОТОГРАФИЯ ВОЗВРАЩАЕТСЯ ПЕРВОЙ И ВСЕГДА.
     *
     * Владелец поймал на живой теме: наведение на второй свотч меняло фотографию, а увод
     * мыши её НЕ возвращал — на экране синий камень при выбранном и подписанном красном.
     * Подпись при этом возвращалась, то есть до конца функции исполнение доходило.
     *
     * Здесь две отдельные причины, и закрыты обе:
     *
     * 1. ПОРЯДОК. Раньше перед возвратом фотографии шли два morph() — цены и спеков — без
     *    try/catch. Любое исключение внутри них оставляло на экране чужой снимок, а это
     *    единственное, что покупатель реально видит. Теперь фотография восстанавливается
     *    ПЕРВОЙ, а морфы обёрнуты и упасть молча уже не могут.
     *
     * 2. ОТКРЕПЛЁННЫЙ УЗЕЛ. `originalImageElement` запоминается на наведении. Если между
     *    наведением и уводом этот <img> заменили — морфом цены/спеков или перерисовкой
     *    слайда, — `src` возвращался в узел, которого в документе уже нет, и на экране
     *    оставался предпросмотр. Теперь узел проверяется на связь с документом и при
     *    необходимости ищется заново в живой галерее.
     */
    if (this.originalImageSrc) {
      let img = this.originalImageElement;
      if (!img || !img.isConnected) {
        img =
          section.querySelector('media-gallery slideshow-slide[aria-hidden="false"] img') ||
          section.querySelector('.media-gallery__grid .product-media img') ||
          section.querySelector('media-gallery img');
      }
      if (img) {
        img.src = this.originalImageSrc;
        if (this.originalImageSrcset !== undefined) {
          img.srcset = this.originalImageSrcset || '';
        }

        if (this.originalPictureSources) {
          this.originalPictureSources.forEach((item) => {
            if (item.el && item.el.isConnected && item.srcset) item.el.setAttribute('srcset', item.srcset);
          });
        }
      }
      this.originalImageSrc = null;
      this.originalImageSrcset = null;
      this.originalPictureSources = null;
      this.originalImageElement = null;
    }

    if (this.originalPriceHTML) {
      try {
        if (priceContainer) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(this.originalPriceHTML, 'text/html');
          const originalPriceElement = doc.querySelector(`[ref='priceContainer']`) || doc.body.firstElementChild;
          if (originalPriceElement) {
            morph(priceContainer, originalPriceElement);
          }
        }
      } catch (error) {
        console.warn('[variant-picker] возврат цены не удался:', error);
      }
      this.originalPriceHTML = null;
    }

    if (this.originalSpecsHTML) {
      try {
        const specList = section.querySelector('r69-product-specs [data-r69spec-list]');
        if (specList) {
          const doc = new DOMParser().parseFromString(this.originalSpecsHTML, 'text/html');
          const originalSpecs = doc.querySelector('[data-r69spec-list]');
          if (originalSpecs) morph(specList, originalSpecs);
        }
      } catch (error) {
        console.warn('[variant-picker] возврат спеков не удался:', error);
      }
      this.originalSpecsHTML = null;
    }

    if (this.originalMaterialHTML) {
      try {
        const materialLine = section.querySelector('.product-details [data-material-line]');
        if (materialLine) {
          const doc = new DOMParser().parseFromString(this.originalMaterialHTML, 'text/html');
          const originalMaterial = doc.querySelector('[data-material-line]');
          if (originalMaterial) morph(materialLine, originalMaterial);
        }
      } catch (error) {
        console.warn('[variant-picker] возврат строки материала не удался:', error);
      }
      this.originalMaterialHTML = null;
    }

    if (this.originalSwatchValueElement) {
      this.originalSwatchValueElement.innerHTML = this.originalSwatchValueHTML;
      this.originalSwatchValueElement = null;
      this.originalSwatchValueHTML = null;
    }

    if (mediaGallery) {
      if (mediaGallery.slideshow) {
        const slideshow = mediaGallery.slideshow;
        const selectedImageId = this.selectedOption?.dataset.optionMediaId;
        const initialSlide = slideshow.initialSlide;
        const slideId = selectedImageId || initialSlide?.getAttribute('slide-id');

        if (slideId) {
          const slides = slideshow.refs.slides || [];
          const targetSlide = slides.find((/** @type {any} */ slide) => slide.getAttribute('slide-id') == slideId);
          if (targetSlide) {
            targetSlide.hidden = false;
            targetSlide.setAttribute('reveal', '');
            targetSlide.setAttribute('aria-hidden', 'false');

            for (const slide of slides) {
              if (slide !== targetSlide) {
                slide.setAttribute('aria-hidden', 'true');
                slide.removeAttribute('reveal');
                if (slide.getAttribute('variant-image') != null) {
                  slide.hidden = true;
                }
              }
            }

            if (slideshow.select) {
              slideshow.select({ id: slideId.toString() }, undefined, { animate: false }).catch(() => {});
            }
          }
        }
      } else if (mediaGallery.media) {
        const selectedImageId = this.selectedOption?.dataset.optionMediaId;
        if (selectedImageId) {
          const targetMedia = Array.from(mediaGallery.media || []).find(
            (/** @type {any} */ el) => el.getAttribute('data-media-id') == selectedImageId
          );
          if (targetMedia) {
            targetMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }
    }
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
